import Link from "next/link";
import { LuMessagesSquare, LuPin, LuRepeat } from "react-icons/lu";

import { TimestampTooltip } from "@/components/shared/timestamp-tooltip";
import { useFeedInternalContext } from "@/contexts/feed-context";
import { usePostContext } from "@/contexts/post-context";
import { cn } from "@/utils/lib";
import type { PartialAccount } from "@/utils/types";
import { getUserName } from "@/utils/usernames";

type PostStatusRowProps = {
  section?: "feed" | "detail";
  className?: string;
};

const RepostContent = ({
  account,
  timestamp,
}: {
  account: PartialAccount;
  timestamp: string;
}) => (
  <>
    <LuRepeat />
    Reposted by{" "}
    <Link
      className="
        -ml-px underline-offset-4
        hover:underline
      "
      href={`/${account?.username ?? account?.publicKey}`}
      prefetch={false}
    >
      {getUserName({ account })}
    </Link>{" "}
    <TimestampTooltip timestamp={timestamp} />
  </>
);

const RemovedContent = "The parent post was removed by the author.";

const ReplyRow = ({
  replyRecipient,
}: {
  replyRecipient?: PartialAccount | null;
}) => {
  if (!replyRecipient) return null;

  const { username, publicKey } = replyRecipient;

  return (
    <>
      <LuMessagesSquare />
      Replying to{" "}
      <Link
        href={`/${username ?? publicKey}`}
        className="
          -ml-px underline-offset-4
          hover:underline
        "
        prefetch={false}
      >
        {getUserName({ account: replyRecipient })}
      </Link>
    </>
  );
};

const PinnedContent = (
  <div className="flex items-center gap-2 text-primary">
    <LuPin />
    Pinned
  </div>
);

export const PostStatusRow = ({ section, className }: PostStatusRowProps) => {
  const {
    isPinned,
    hideStatusRow,
    isPureRepost,
    isParentPostRemoved,
    isReply,
    post,
    replyRecipient,
  } = usePostContext();

  // Note: context can be absent on Specific post page
  const internalFeedContext = useFeedInternalContext();
  const isAdminPinnedPost =
    internalFeedContext?.isAdminPinnedPost ?? (() => false);

  if (hideStatusRow) return null;

  const getRowContent = () => {
    if (isPureRepost && post?.poster) {
      return (
        <RepostContent account={post?.poster} timestamp={post.timestamp} />
      );
    }
    if (isParentPostRemoved) {
      return RemovedContent;
    }
    if (isReply) {
      return <ReplyRow replyRecipient={replyRecipient} />;
    }
    return null;
  };

  const rowContent = getRowContent();

  const pinnedContent =
    isPinned || isAdminPinnedPost(post.postHash ?? "") ? PinnedContent : null;

  if (!pinnedContent && !rowContent) return null;

  return (
    <div
      className={cn(
        `
          mb-0 flex items-center gap-2 pt-4 text-sm text-muted
          sm:-mb-2 sm:pt-6
        `,
        {
          "pl-0 md:-ml-2 md:pl-0": section === "feed",
          "pl-6 md:ml-8 md:pl-0": section === "detail",
        },
        className,
      )}
    >
      {pinnedContent}
      {rowContent}
    </div>
  );
};
